import {
  Component,
  Input,
  Output,
  EventEmitter,
  ContentChildren,
  QueryList,
  AfterContentInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
  ComponentRef,
  Type,
  OnInit,
  ChangeDetectorRef,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { StepperModule } from 'primeng/stepper';
import { ButtonModule } from 'primeng/button';
import { TranslateModule } from '@ngx-translate/core';
import { PublicService } from '../../../trash/shared/services/public.service';

export interface StepperStep {
  id: string;
  title: string;
  subtitle?: string;
  icon?: string;
  isCompleted?: boolean; // if the step is completed
  showBackButton?: boolean;
  showNextButton?: boolean;
  showCancelButton?: boolean;
  cancelButtonLabel?: string;
  nextButtonLabel?: string;
  backButtonLabel?: string;
  nextButtonDisabled?: boolean; // if the next button is disabled
  nextButtonLoading?: boolean; // if the next button should show loading state
  component: Type<any>; // the component to be rendered for the step
  showSelectedItem?: boolean; // if the selected item should be shown in header
  selectedItemKey?: string; // the key to get selected item from step data
  actions?: {
    onNext?: (stepIndex: number, step: StepperStep, data?: any) => void; // action to be executed when the next button is clicked
    onBack?: (stepIndex: number, step: StepperStep, data?: any) => void; // action to be executed when the back button is clicked
    onStepComplete?: (stepIndex: number, step: StepperStep, data?: any) => void; // action to be executed when the step is completed
    onStepChange?: (stepIndex: number, step: StepperStep, data?: any) => void; // action to be executed when the step is changed
    onCancel?: (stepIndex: number, step: StepperStep, data?: any) => void; // action to be executed when the cancel button is clicked
  };
}

export interface StepperConfig {
  title?: string;
  steps: StepperStep[]; // the steps to be rendered
  showStepNumbers?: boolean; // if the step numbers should be shown
  allowStepNavigation?: boolean; // if the step navigation should be allowed
  linear?: boolean; // if the stepper should be linear or not
  orientation?: 'horizontal' | 'vertical'; // the orientation of the stepper
}

@Component({
  selector: 'app-shared-stepper',
  imports: [CommonModule, StepperModule, ButtonModule, TranslateModule],
  templateUrl: './shared-stepper.component.html',
  styleUrl: './shared-stepper.component.scss'
})
export class SharedStepperComponent implements OnInit, AfterContentInit, OnChanges {
  cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  publicService: PublicService = inject(PublicService);

  @Input() config!: StepperConfig;
  @Input() activeStep: number = 0;
  @Input() showBackButton: boolean = true;
  @Input() showNextButton: boolean = true;
  @Input() cancelButtonLabel: string = 'GENERAL.CANCEL';
  @Input() nextButtonLabel: string = 'GENERAL.NEXT';
  @Input() backButtonLabel: string = 'GENERAL.BACK';
  @Input() nextButtonDisabled: boolean = false;
  @Input() nextButtonLoading: boolean = false;

  @Output() stepChange = new EventEmitter<number>();
  @Output() stepComplete = new EventEmitter<{ stepIndex: number; step: StepperStep; data?: any }>();
  @Output() nextStep = new EventEmitter<{ stepIndex: number; step: StepperStep }>();
  @Output() previousStep = new EventEmitter<{ stepIndex: number; step: StepperStep }>();
  @Output() stepperComplete = new EventEmitter<any>();

  @ContentChildren('stepContent') stepContents!: QueryList<any>;
  @ViewChild('dynamicContent', { read: ViewContainerRef }) dynamicContent!: ViewContainerRef;

  currentStep: number = 0;
  stepData: any = {};
  componentRefs: Map<number, ComponentRef<any>> = new Map();

  ngOnInit() {
    this.currentStep = this.activeStep;
  }

  ngAfterContentInit() {
    // Wait for the next tick to ensure ViewChild is available
    setTimeout(() => {
      this.renderCurrentStepComponent();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['activeStep']) {
      this.currentStep = this.activeStep;
      setTimeout(() => {
        this.renderCurrentStepComponent();
      });
    }
  }

  onStepChange(stepIndex: number) {
    if (this.config.allowStepNavigation !== false) {
      this.currentStep = stepIndex;
      setTimeout(() => {
        this.renderCurrentStepComponent();
      });
      this.stepChange.emit(stepIndex);

      // Call step's onStepChange action if defined
      const step = this.config.steps[stepIndex];
      if (step?.actions?.onStepChange) {
        step.actions.onStepChange(stepIndex, step, this.getStepData(stepIndex));
      }
    }
  }

  onCancelStep() {
    const currentStepData = this.config.steps[this.currentStep];
    if (currentStepData?.actions?.onCancel) {
      currentStepData.actions.onCancel(this.currentStep, currentStepData, this.getStepData(this.currentStep));
    }
  }

  onNextStep() {
    const currentStepData = this.config.steps[this.currentStep];

    // Call step's onNext action if defined
    if (currentStepData?.actions?.onNext) {
      currentStepData.actions.onNext(this.currentStep, currentStepData, this.getStepData(this.currentStep));
    }

    this.nextStep.emit({ stepIndex: this.currentStep, step: currentStepData });

    if (this.currentStep < this.config.steps.length - 1) {
      this.currentStep++;
      setTimeout(() => {
        this.renderCurrentStepComponent();
      });
      this.stepChange.emit(this.currentStep);
    } else {
      this.stepperComplete.emit(this.stepData);
    }
  }

  onPreviousStep() {
    const currentStepData = this.config.steps[this.currentStep];

    // Call step's onBack action if defined
    if (currentStepData?.actions?.onBack) {
      currentStepData.actions.onBack(this.currentStep, currentStepData, this.getStepData(this.currentStep));
    }

    this.previousStep.emit({ stepIndex: this.currentStep, step: currentStepData });

    if (this.currentStep > 0) {
      for (let i = this.currentStep - 1; i < this.config.steps.length; i++) {
        this.clearStepData(i);
      }

      this.currentStep--;
      setTimeout(() => {
        this.renderCurrentStepComponent();
        this.cdr.detectChanges();
      });
      this.stepChange.emit(this.currentStep);
    }
  }

  onStepComplete(stepIndex: number) {
    const step = this.config.steps[stepIndex];
    step.isCompleted = true;

    // Call step's onStepComplete action if defined
    if (step?.actions?.onStepComplete) {
      step.actions.onStepComplete(stepIndex, step, this.getStepData(stepIndex));
    }

    this.stepComplete.emit({ stepIndex, step });
  }

  isStepCompleted(stepIndex: number): boolean {
    return this.config.steps[stepIndex]?.isCompleted || false;
  }

  getStepNumber(stepIndex: number): string {
    return (stepIndex + 1).toString();
  }

  updateStepData(stepIndex: number, data: any) {
    this.stepData[stepIndex] = { ...this.stepData[stepIndex], ...data };
    this.cdr.detectChanges();
  }

  getStepData(stepIndex: number): any {
    return this.stepData[stepIndex] || {};
  }

  // Get all accumulated step data
  public getAllStepData(): any {
    console.log('All step data:', this.stepData);
    return this.stepData;
  }

  // Public methods for parent components to update step data
  public setStepData(stepIndex: number, data: any) {
    this.updateStepData(stepIndex, data);
  }

  public clearStepData(stepIndex: number) {
    this.stepData[stepIndex] = {};
    // Reset step completion status when clearing data
    if (this.config.steps[stepIndex]) {
      this.config.steps[stepIndex].isCompleted = false;
    }
    this.cdr.detectChanges();
  }

  public setStepCompleted(stepIndex: number, isCompleted: boolean) {
    if (this.config.steps[stepIndex]) {
      this.config.steps[stepIndex].isCompleted = isCompleted;
    }
  }

  public setStepButtonDisabled(stepIndex: number, disabled: boolean) {
    if (this.config.steps[stepIndex]) {
      this.config.steps[stepIndex].nextButtonDisabled = disabled;
    }
  }

  public setStepButtonLoading(stepIndex: number, loading: boolean) {
    if (this.config.steps[stepIndex]) {
      this.config.steps[stepIndex].nextButtonLoading = loading;
      this.cdr.detectChanges();
    }
  }

  // Dynamic component rendering
  private renderCurrentStepComponent() {
    if (!this.dynamicContent) {
      console.warn('Dynamic content container not available');
      return;
    }

    const currentStep = this.config.steps[this.currentStep];
    if (!currentStep?.component) {
      console.warn('No component defined for current step:', this.currentStep);
      return;
    }

    try {
      // Clear existing content
      this.dynamicContent.clear();
      this.componentRefs.clear();

      // Create new component
      const componentRef = this.dynamicContent.createComponent(currentStep.component);

      // Pass accumulated data as inputs to the component
      this.passDataToComponent(componentRef, currentStep);

      // Handle component outputs
      this.handleComponentOutputs(componentRef, currentStep);

      // Store reference
      this.componentRefs.set(this.currentStep, componentRef);
    } catch (error) {
      console.error('Error rendering component for step:', this.currentStep, error);
    }
  }

  // Pass accumulated data as inputs to the component
  private passDataToComponent(componentRef: ComponentRef<any>, step: StepperStep) {
    const componentInstance = componentRef.instance;

    // Pass all accumulated step data to the component
    const allStepData = this.getAllStepData();

    // Pass each step's data as a separate input property
    Object.keys(allStepData).forEach((stepIndex) => {
      const stepData = allStepData[stepIndex];
      if (stepData && Object.keys(stepData).length > 0) {
        // Pass the entire step data object
        componentInstance[`step${stepIndex}Data`] = stepData;

        // Also pass individual properties from the step data
        Object.keys(stepData).forEach((key) => {
          componentInstance[key] = stepData[key];
        });
      }
    });

    // Pass the data as a single property
    componentInstance.allStepData = allStepData;
  }

  // Handle component outputs and emit them to parent
  private handleComponentOutputs(componentRef: ComponentRef<any>, step: StepperStep) {
    const componentInstance = componentRef.instance;
    // Handle any output that emits step data
    if (componentInstance.stepDataChange) {
      componentInstance.stepDataChange.subscribe((data: any) => {
        // Update step data with whatever the component emits
        this.setStepData(this.currentStep, data);

        // Emit to parent component
        this.stepComplete.emit({
          stepIndex: this.currentStep,
          step: step,
          data: data
        });
      });
    }

    // Handle step completion
    if (componentInstance.stepComplete) {
      componentInstance.stepComplete.subscribe((data: any) => {
        this.setStepCompleted(this.currentStep, true);

        this.stepComplete.emit({
          stepIndex: this.currentStep,
          step: step,
          data: data
        });
      });
    }

    // Handle next step signal
    if (componentInstance.nextStep) {
      componentInstance.nextStep.subscribe(() => {
        this.onNextStep();
      });
    }

    // Handle form validity changes
    if (componentInstance.formValidityChange) {
      componentInstance.formValidityChange.subscribe((isValid: boolean) => {
        this.setStepButtonDisabled(this.currentStep, !isValid);
      });
    }

    // Handle start date changes
    if (componentInstance.startDateChange) {
      componentInstance.startDateChange.subscribe((startDate: any) => {
        // Update step data with the start date
        this.setStepData(this.currentStep, { selectedStartDate: startDate });

        // Emit to parent component
        this.stepComplete.emit({
          stepIndex: this.currentStep,
          step: step,
          data: { selectedStartDate: startDate }
        });
      });
    }

    // Handle payment method changes
    if (componentInstance.paymentMethodChange) {
      componentInstance.paymentMethodChange.subscribe((paymentMethod: string) => {
        // Update step data with the payment method
        this.setStepData(this.currentStep, { selectedPaymentMethod: paymentMethod });

        // Emit to parent component
        this.stepComplete.emit({
          stepIndex: this.currentStep,
          step: step,
          data: { selectedPaymentMethod: paymentMethod }
        });
      });
    }
  }

  ngOnDestroy() {
    // Clean up component references
    this.componentRefs.forEach((ref) => ref.destroy());
    this.componentRefs.clear();
  }
}
